/**
 * De-duplicates session-details calls: the purchase tracker and the confirmation page both need the
 * same session. Only COMPLETE sessions are cached — an OPEN one can still transition, and a retry
 * after a failure must reach the API again.
 */
import { isAxiosError } from 'axios'
import type { User } from 'firebase/auth'

import type { AuthSource } from '@/lib/getUserAuthHeaders'
import { getSessionStatus } from '@/lib/payment-service'

import type { GetCheckoutSessionDetailsResponse } from '@/clients/payment-service'

import { getIntentCheckout, IntentCheckoutRequestError } from './intent/api'
import type { IntentCheckoutResponse } from './intent/types'
import { isIntentPaid } from './intentPurchase'
import type { CheckoutCountry } from './types'

const cache = new Map<string, Promise<GetCheckoutSessionDetailsResponse>>()

const cacheKey = (countryCode: string, sessionId: string, guestEmail?: string) =>
  `${countryCode}:${sessionId}:${guestEmail ?? ''}`

export const fetchSessionDetails = (
  auth: AuthSource,
  countryCode: CheckoutCountry,
  sessionId: string,
  guestEmail?: string,
): Promise<GetCheckoutSessionDetailsResponse> => {
  const key = cacheKey(countryCode, sessionId, guestEmail)
  const cached = cache.get(key)
  if (cached) return cached

  const pending = getSessionStatus(auth, countryCode, sessionId, guestEmail)
  cache.set(key, pending)
  void pending
    .then((session) => {
      if (session.status !== 'COMPLETE') cache.delete(key)
    })
    .catch(() => cache.delete(key))
  return pending
}

/**
 * As {@link fetchSessionDetails}, retrying a failed call with a widening delay. For callers that
 * gate what a customer sees on the answer, where one transient failure should not decide it.
 */
export const fetchSessionDetailsWithRetry = async (
  auth: AuthSource,
  countryCode: CheckoutCountry,
  sessionId: string,
  { retries = 2, delayMs = 500, guestEmail }: { retries?: number; delayMs?: number; guestEmail?: string } = {},
): Promise<GetCheckoutSessionDetailsResponse> => {
  try {
    return await fetchSessionDetails(auth, countryCode, sessionId, guestEmail)
  } catch (error) {
    if (retries <= 0) throw error
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    return fetchSessionDetailsWithRetry(auth, countryCode, sessionId, {
      retries: retries - 1,
      delayMs: delayMs * 2,
      guestEmail,
    })
  }
}

const INTENT_POLL_INTERVAL_MS = 2000
const INTENT_POLL_TIMEOUT_MS = 60_000

/**
 * Why a poll gave up before the payment was seen. `still_confirming` is the one case where the customer may
 * have paid: payment-service holds the confirm lease and the webhook will finish it, so nothing must invite a
 * second payment.
 */
export type IntentPollReason = 'expired' | 'still_confirming' | 'timed_out'

export class IntentPollError extends Error {
  constructor(
    readonly reason: IntentPollReason,
    message: string,
  ) {
    super(message)
    this.name = 'IntentPollError'
  }
}

const intentPolls = new Map<string, Promise<IntentCheckoutResponse>>()

// A 408 or 429 is a blip, like a 5xx.
const isClientRejection = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  return status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429
}

const pollUntilPaid = async (user: User, id: string): Promise<IntentCheckoutResponse> => {
  const deadline = Date.now() + INTENT_POLL_TIMEOUT_MS
  let lastStatus: IntentCheckoutResponse['status'] | null = null
  for (;;) {
    let intent: IntentCheckoutResponse | null = null
    try {
      intent = await getIntentCheckout(user, id)
    } catch (error) {
      // A rejection (4xx, or a 410 for an expired reference) will not change; a blip will.
      if (error instanceof IntentCheckoutRequestError || isClientRejection(error)) throw error
    }
    if (intent) lastStatus = intent.status
    if (intent && isIntentPaid(intent.status)) return intent
    if (intent?.status === 'EXPIRED') throw new IntentPollError('expired', `Checkout ${id} expired`)
    if (Date.now() >= deadline) {
      throw new IntentPollError(
        lastStatus === 'CONFIRMING' ? 'still_confirming' : 'timed_out',
        `Timed out waiting for checkout ${id} to be paid (last status ${lastStatus ?? 'unknown'})`,
      )
    }
    await new Promise((resolve) => setTimeout(resolve, INTENT_POLL_INTERVAL_MS))
  }
}

/**
 * Polls a reference until it is paid, shared by the purchase tracker and the confirmation page. Rejects with an
 * {@link IntentPollError} on `EXPIRED` or the cap, or with the 4xx itself; a rejected poll is dropped so a later caller starts afresh.
 * `CONFIRM_NEEDS_ACTION` is not paid and a read never moves it, so the poll keeps going to the cap.
 */
export const pollIntentCheckout = (user: User, id: string): Promise<IntentCheckoutResponse> => {
  const existing = intentPolls.get(id)
  if (existing) return existing

  const poll = pollUntilPaid(user, id)
  intentPolls.set(id, poll)
  poll.catch(() => intentPolls.delete(id))
  return poll
}
